import { Grid, Alert } from "@mui/material";
import React, { useEffect, useState } from "react";

import FormSelect from "../FormSelect";
import FormTextField from "../FormTextField";
import FormRadioGroup from "../FormRadioGroup";
import { useCities } from "../../../hooks/useCities";
import { useEmiAdminConfig } from "../../../hooks/useEmiAdminConfig";

import {
  roundUpToStep,
  buildTenureOptions,
  getAverageInterestRate,
  buildDownpaymentOptions,
} from "../config/emiConfig";
import { isPhoneNumberUnique } from "../utils/leadFormSchemas";

const PHONE_CHECK_DEBOUNCE_MS = 500;

const DriverInformationStep = ({
  data,
  onChange,
  showErrors,
  errors,
  setErrors,
  clearFieldError,
  onPhoneCheckStatusChange,
}) => {
  const { data: emiAdminConfig } = useEmiAdminConfig("v2");
  const { data: cities, isLoading: isCitiesLoading } = useCities();

  const [phoneCheckError, setPhoneCheckError] = useState("");

  useEffect(() => {
    const phoneNumber = String(data?.phoneNumber || "").trim();

    setPhoneCheckError("");
    onPhoneCheckStatusChange("idle");

    if (!/^\d{10}$/.test(phoneNumber)) {
      return undefined;
    }

    onPhoneCheckStatusChange("checking");

    const timer = setTimeout(async () => {
      try {
        const unique = await isPhoneNumberUnique(phoneNumber);
        if (unique) {
          onPhoneCheckStatusChange("unique");
        } else {
          setErrors((prevErrors) => ({
            ...prevErrors,
            phoneNumber: "This phone number is already in use",
          }));
          setPhoneCheckError("This phone number is already in use");
          onPhoneCheckStatusChange("taken");
        }
      } catch (err) {
        setPhoneCheckError("Failed to verify phone number");
        onPhoneCheckStatusChange("error");
      }
    }, PHONE_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [data?.phoneNumber]);

  const fieldError = (field) => (showErrors && errors?.[field]) || "";
  const phoneNumberError = phoneCheckError || fieldError("phoneNumber");

  const downpaymentOptions = buildDownpaymentOptions(
    emiAdminConfig?.downPayment,
  );
  const tenureOptions = buildTenureOptions(emiAdminConfig?.tenures);

  const loanPrincipal =
    Number(data.invoiceValue || 0) - Number(data.downpayment || 0);
  const averageInterestRate = getAverageInterestRate(emiAdminConfig);
  const primaryTenure = emiAdminConfig?.tenures?.[0];

  const illustrativeEmi =
    averageInterestRate && primaryTenure && loanPrincipal > 0
      ? roundUpToStep(
          (loanPrincipal *
            (averageInterestRate / 12) *
            Math.pow(1 + averageInterestRate / 12, primaryTenure)) /
            (Math.pow(1 + averageInterestRate / 12, primaryTenure) - 1),
          5,
        )
      : null;

  const updateFinancier = (key, value) => {
    onChange(
      "vehicleFinancierDetails",
      { ...data.vehicleFinancierDetails, [key]: value },
      `vehicleFinancierDetails.${key}`,
    );
  };

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} sm={6}>
        <FormTextField
          label="Driver Name"
          required
          value={data.driverName}
          onChange={(value) => onChange("driverName", value)}
          error={fieldError("driverName")}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormTextField
          label="Driver Phone Number"
          required
          type="tel"
          maxLength={10}
          value={data.phoneNumber}
          onChange={(value) => onChange("phoneNumber", value)}
          error={phoneNumberError}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormTextField
          label="Driver Age"
          required
          type="number"
          value={data.age}
          onChange={(value) => onChange("age", value)}
          error={fieldError("age")}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormSelect
          label="City"
          required
          value={data.city}
          onChange={(value) => onChange("city", value)}
          options={
            isCitiesLoading
              ? []
              : cities.map((item) => ({ label: item, value: item }))
          }
          error={fieldError("city")}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormTextField
          label="Invoice Value to Driver"
          required
          type="number"
          value={data.invoiceValue}
          onChange={(value) => onChange("invoiceValue", value)}
          error={fieldError("invoiceValue")}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormSelect
          label="Downpayment"
          required
          value={data.downpayment}
          onChange={(value) => onChange("downpayment", value)}
          options={downpaymentOptions.map((value) => ({
            label: `₹${value.toLocaleString("en-IN")}`,
            value,
          }))}
          error={fieldError("downpayment")}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormSelect
          label="EMI Tenure"
          required
          value={data.emiTenure}
          onChange={(value) => onChange("emiTenure", value)}
          options={tenureOptions}
          error={fieldError("emiTenure")}
        />
      </Grid>

      {illustrativeEmi && (
        <Grid item xs={12}>
          <Alert severity="info">
            Illustrative EMI (blended average rate, subject to actual
            CIBIL-based financier rate): ₹
            {illustrativeEmi.toLocaleString("en-IN")} / month
          </Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        <FormRadioGroup
          label="Vehicle Financed?"
          required
          value={data.vehicleFinanced}
          onChange={(value) => {
            onChange("vehicleFinanced", value);
            if (value === "No") {
              onChange("vehicleFinancierDetails", undefined);
              clearFieldError("vehicleFinancierDetails", {
                includeNested: true,
              });
            }
          }}
          options={["Yes", "No"]}
          error={fieldError("vehicleFinanced")}
        />
      </Grid>

      {data.vehicleFinanced === "Yes" && (
        <>
          <Grid item xs={12} sm={6}>
            <FormTextField
              label="Vehicle Financier Name"
              required
              value={data.vehicleFinancierDetails?.name || ""}
              onChange={(value) => updateFinancier("name", value)}
              error={fieldError("vehicleFinancierDetails.name")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormTextField
              label="Financier Phone Number"
              required
              type="tel"
              maxLength={10}
              value={data.vehicleFinancierDetails?.phoneNumber || ""}
              onChange={(value) => updateFinancier("phoneNumber", value)}
              error={fieldError("vehicleFinancierDetails.phoneNumber")}
            />
          </Grid>

          <Grid item xs={12}>
            <FormTextField
              label="Financier Address"
              required
              multiline
              rows={2}
              value={data.vehicleFinancierDetails?.address || ""}
              onChange={(value) => updateFinancier("address", value)}
              error={fieldError("vehicleFinancierDetails.address")}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default DriverInformationStep;
